import { BookmarkIcon, PhoneIcon } from 'lucide-react';
import { DetailsPageIsland } from '@rocco/ui/floating-island/components/detailspage-island/DetailsPageIsland';
import { ReactComponent as EnquiryIcon } from '@rocco/icons/svg/message.svg';
import { useRootData } from '~/modules/root';
import { Button } from '@rocco/components/button';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { cn } from '@rocco/utils/cn';
import { type DetailsPageIslandImageProps } from '@rocco/ui/floating-island/components/detailspage-island/DetailsPageIslandImage';
import { useEnquiryModal } from '~/modules-rocco/enquiry/hooks/use-enquiry-modal';
import { type ProductCTAResult } from '~/modules/professional/util/professional-cta-helper';

import { usePhoneTracking } from '~/modules/tracking/hook/usePhoneTracking';
import { FloatingIsland } from '../../../../../../rocco/src/ui/floating-island/components/FloatingIsland';

interface ProductIslandProps {
    product: Pick<
        ProductPageLoaderData['product'],
        'ID' | 'Title' | 'DefaultImage'
    >;
    professional: Pick<
        ProductPageLoaderData['product']['Professional'],
        | 'ID'
        | 'Title'
        | 'Link'
        | 'CompanyLogo'
        | 'LogoBackgroundColor'
        | 'branchOptions'
    >;
    productCTA?: ProductCTAResult;
    showProductImage?: boolean;
}

export const ProductIsland = ({
    product,
    professional,
    productCTA,
    showProductImage = false,
}: ProductIslandProps) => {
    const { Title: productTitle, ID: productId } = product;
    const {
        Title: professionalTitle,
        Link: professionalLink,
        CompanyLogo,
        LogoBackgroundColor,
    } = professional;

    const { appDisplayMode } = useRootData();

    const { openEnquiryModal } = useEnquiryModal({
        title: getproductCTAText(productCTA),
        professional,
        itemType: 'product',
        siteTreeID: productId,
        source: 'ProductIsland',
        ctaLabel: getproductCTAText(productCTA),
    });

    const {
        enabled,
        phone: phoneWithTracking,
        handlePhoneClick,
        phoneLabel,
    } = usePhoneTracking();

    const showPhoneTrackingCTA = enabled && !!phoneWithTracking;

    const image: DetailsPageIslandImageProps = showProductImage
        ? {
              image: {
                  src:
                      product.DefaultImage?.LibraryImageFile?.DetailPageSquare
                          ?.Desktop ?? '',
                  alt: product.Title,
                  multiplied: product.DefaultImage?.IsMultiplied,
              },
              size: 'full',
          }
        : {
              image: {
                  src: CompanyLogo,
                  alt: professionalTitle,
              },
              background: LogoBackgroundColor,
              link: professionalLink,
              size: 'professionalLogo',
          };

    if (appDisplayMode === DisplayMode.Mobile && !showPhoneTrackingCTA) {
        return null;
    }

    if (appDisplayMode === DisplayMode.Mobile && showPhoneTrackingCTA) {
        return (
            <FloatingIsland scrollThreshold={100} variant="transparent">
                <Button
                    type="button"
                    size={'default'}
                    color="primary"
                    onClick={handlePhoneClick}
                    className="w-full !border-none !bg-[#232323]/60 !text-white hover:!bg-[#232323]/85"
                >
                    <span className="mr-2.5 flex items-center">
                        {`Call us today`}
                    </span>
                    <PhoneIcon
                        aria-label={`Call us today`}
                        style={{
                            transform: 'scaleX(-1)',
                        }}
                        className="w-4 fill-white"
                    />
                </Button>
            </FloatingIsland>
        );
    }

    return (
        <DetailsPageIsland
            key={`product-island-${productId}`}
            isClickable={false}
            scrollThreshold={0}
            image={image}
            displayMode={appDisplayMode}
            title={productTitle}
            description={professionalTitle}
            primaryAction={
                <ProductIslandEnquiryButton
                    label={getproductCTAText(productCTA)}
                    onClick={openEnquiryModal}
                    displayMode={appDisplayMode}
                    icon={
                        <EnquiryIcon
                            aria-label={`${getproductCTAText(productCTA)} for ${professional.Title}`}
                            className="w-4 fill-white"
                        />
                    }
                    type="primary"
                />
            }
            secondaryAction={
                showPhoneTrackingCTA ? (
                    <ProductIslandCallButton
                        label={phoneLabel ?? 'Call Us'}
                        onClick={handlePhoneClick}
                        displayMode={appDisplayMode}
                    />
                ) : (
                    <ProductIslandAddToDesignBoardButton
                        label="Shortlist Product"
                        displayMode={appDisplayMode}
                        productId={productId}
                    />
                )
            }
        />
    );
};

const ProductIslandEnquiryButton = ({
    label,
    onClick,
    displayMode,
    icon,
    type,
}: {
    label: string;
    onClick: () => void;
    displayMode: DisplayMode;
    icon: React.ReactNode;
    type: 'primary' | 'secondary';
}) => {
    const compact = displayMode === DisplayMode.Mobile;

    return (
        <Button
            type="button"
            size={compact ? 'square42' : 'default'}
            color={type === 'primary' ? 'primary' : 'secondary70'}
            onClick={onClick}
            className={
                '!border-none !bg-[#232323]/60 !text-white hover:!bg-[#232323]/85'
            }
        >
            <span
                className={`flex items-center gap-2 ${type === 'primary' ? 'fill-white' : ''}`}
            >
                {compact ? null : label}
                {icon}
            </span>
        </Button>
    );
};

const ProductIslandAddToDesignBoardButton = ({
    label,
    displayMode,
    productId,
}: {
    label: string;
    displayMode: DisplayMode;
    productId: number;
}) => {
    const compact = displayMode === DisplayMode.Mobile;
    const { isInDesignBoard, handleFollow } = useHandleFollow();
    const saved = isInDesignBoard(productId, 'ProductPin');
    return (
        <Button
            type="button"
            size={compact ? 'square42' : 'default'}
            color="secondary70"
            onClick={() =>
                handleFollow({ id: productId }, 'ProductPin', 'ProductIsland')
            }
        >
            <span className="flex items-center gap-2">
                {compact ? null : saved ? 'Shortlisted' : 'Shortlist'}
                <BookmarkIcon
                    aria-label={label}
                    className={cn(
                        'w-4',
                        saved ? 'fill-gray-900' : 'stroke-gray-900'
                    )}
                />
            </span>
        </Button>
    );
};

const ProductIslandCallButton = ({
    label,
    onClick,
    displayMode,
}: {
    label: string;
    onClick: () => void;
    displayMode: DisplayMode;
}) => {
    const compact = displayMode === DisplayMode.Mobile;

    return (
        <Button
            type="button"
            size={compact ? 'square42' : 'default'}
            color="white"
            onClick={onClick}
        >
            <span className="flex items-center gap-2">
                {compact ? null : label}
                <PhoneIcon aria-label={label} className="w-4 fill-white" />
            </span>
        </Button>
    );
};

const getproductCTAText = (cta: ProductCTAResult | undefined): string => {
    return cta?.productCTALabel || 'Request Price / Quote';
};
